import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, Modal, Spinner } from 'react-bootstrap'
import { motion, AnimatePresence } from 'framer-motion'
import { Formik, Form as FormikForm, Field } from 'formik'
import * as Yup from 'yup'
import {
  FaUsers,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaShieldAlt,
  FaIdCard,
  FaMapMarkerAlt,
  FaSearch,
  FaUserShield,
  FaUserCheck,
} from 'react-icons/fa'
import {
  fetchUsers,
  fetchUser,
  addUser,
  updateUser,
  deleteUser,
  clearSelectedUser,
} from '../../store/slices/userSlice'
import { fetchEntities } from '../../store/slices/entitySlice'
import '../../assets/scss/pages/UserManagement.scss'

const UserSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  phone: Yup.string().nullable(),
  role: Yup.string().required('Role is required'),
  password: Yup.string().when('isEditing', {
    is: false,
    then: () => Yup.string().required('Password is required'),
    otherwise: () => Yup.string().nullable(),
  }),
  aadharNumber: Yup.string().nullable(),
  address: Yup.string().nullable(),
})

const fade = {
  hidden: { opacity: 0, y: 12 },
  visible: i => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.32, ease: 'easeOut' } }),
}

const ROLE_OPTIONS = ['USER', 'ADMIN', 'CO_ADMIN', 'TRUCK_OWNER', 'DRIVER']
const ROLE_COLORS = {
  ADMIN: { bg: 'rgba(20,35,115,0.10)', color: 'rgb(20,35,115)' },
  CO_ADMIN: { bg: 'rgba(245,158,11,0.10)', color: '#b45309' },
  USER: { bg: 'rgba(16,185,129,0.10)', color: '#065f46' },
  TRUCK_OWNER: { bg: 'rgba(13,148,136,0.10)', color: '#0f766e' },
  DRIVER: { bg: 'rgba(239,68,68,0.10)', color: '#991b1b' },
}

const UserManagement = () => {
  const dispatch = useDispatch()
  const { users, selectedUser, loading } = useSelector(state => state.user)

  const [showModal, setShowModal]       = useState(false)
  const [editingUser, setEditingUser]   = useState(null)
  const [viewUser, setViewUser]         = useState(null)
  const [deleteModal, setDeleteModal]   = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [roleFilter, setRoleFilter]     = useState('all')
  const [search, setSearch]             = useState('')

  useEffect(() => {
    dispatch(fetchUsers())
    dispatch(fetchEntities())
  }, [dispatch])

  useEffect(() => {
    if (selectedUser) setViewUser(selectedUser)
  }, [selectedUser])

  const initialValues = useMemo(() => ({
    name:         editingUser?.name         || '',
    email:        editingUser?.email        || '',
    phone:        editingUser?.phone        || '',
    role:         editingUser?.role         || 'USER',
    password:     '',
    aadharNumber: editingUser?.aadharNumber || '',
    address:      editingUser?.address      || '',
    isEditing:    !!editingUser,
  }), [editingUser])

  const handleCloseModal     = () => { setShowModal(false); setEditingUser(null) }
  const handleShowViewModal  = (user) => dispatch(fetchUser(user.id))
  const handleCloseViewModal = () => { setViewUser(null); dispatch(clearSelectedUser()) }

  const confirmDelete = async () => {
    if (userToDelete) {
      await dispatch(deleteUser(userToDelete.id)).unwrap()
      setDeleteModal(false)
      setUserToDelete(null)
    }
  }

  const filteredUsers = useMemo(() => {
    let list = users
    if (roleFilter !== 'all') list = list.filter(u => u.role === roleFilter)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(u =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.phone?.toLowerCase().includes(q)
      )
    }
    return list
  }, [users, roleFilter, search])

  const totalAdmins = users.filter(u => u.role === 'ADMIN' || u.role === 'CO_ADMIN').length
  const totalActive = users.filter(u => u.isActive).length

  return (
    <div className="um-page">

      {/* HEADER */}
      <motion.div custom={0} variants={fade} initial="hidden" animate="visible" className="um-header">
        <div className="um-header-left">
          <div className="um-header-icon"><FaUsers /></div>
          <div>
            <h1 className="um-title">User Management</h1>
            <p className="um-sub">Manage user accounts, roles and access privileges</p>
          </div>
        </div>
        <button
          className="um-add-btn"
          onClick={() => { setEditingUser(null); setShowModal(true) }}
        >
          <FaPlus /> Add User
        </button>
      </motion.div>

      {/* KPI SUMMARY */}
      <Row className="g-3 mb-3">
        {[
          { label: 'Total Users',  value: users.length,  note: 'registered',  icon: <FaUsers />,       color: 'blue'   },
          { label: 'Admins',       value: totalAdmins,   note: 'with admin',  icon: <FaUserShield />,  color: 'purple' },
          { label: 'Active',       value: totalActive,   note: 'operational', icon: <FaUserCheck />,   color: 'green'  },
        ].map((k, i) => (
          <Col key={k.label} xs={12} md={4}>
            <motion.div custom={i + 1} variants={fade} initial="hidden" animate="visible" className={`um-kpi um-kpi--${k.color}`}>
              <div className="um-kpi-left">
                <div className="um-kpi-val">{k.value}</div>
                <div className="um-kpi-label">{k.label}</div>
                <div className="um-kpi-note">{k.note}</div>
              </div>
              <div className="um-kpi-icon">{k.icon}</div>
            </motion.div>
          </Col>
        ))}
      </Row>

      {/* FILTER BAR */}
      <motion.div custom={4} variants={fade} initial="hidden" animate="visible" className="um-filter-bar">
        <div className="um-search-wrap">
          <FaSearch className="um-search-icon" />
          <input
            className="um-search"
            placeholder="Search by name, email or phone…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="um-filter-tabs">
          {['all', ...ROLE_OPTIONS].map(f => (
            <button
              key={f}
              className={`um-tab ${roleFilter === f ? 'um-tab--active' : ''}`}
              onClick={() => setRoleFilter(f)}
            >
              {f === 'all' ? 'All' : f.replace('_', ' ')}
            </button>
          ))}
        </div>
        <span className="um-count">{filteredUsers.length} users</span>
      </motion.div>

      {/* USER LIST */}
      {loading ? (
        <div className="um-loading">
          <Spinner animation="border" style={{ color: 'rgb(20,35,115)' }} />
          <span>Loading users…</span>
        </div>
      ) : filteredUsers.length === 0 ? (
        <motion.div custom={5} variants={fade} initial="hidden" animate="visible" className="um-empty">
          <FaUsers className="um-empty-icon" />
          <p className="um-empty-title">No users found</p>
          <p className="um-empty-sub">Add your first user or adjust the filter above.</p>
        </motion.div>
      ) : (
        <Row className="g-3">
          <AnimatePresence>
            {filteredUsers.map((user, i) => {
              const roleStyle = ROLE_COLORS[user.role] || { bg: '#f4f6fa', color: '#718096' }
              return (
                <Col key={user.id} xs={12} sm={6} md={4} xl={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ delay: i * 0.04, duration: 0.28 }}
                    className="um-card"
                  >
                    <div className="um-card-accent" />
                    <div className="um-card-head">
                      <div className="um-card-avatar">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="um-card-meta">
                        <div className="um-card-name">{user.name}</div>
                        <span
                          className="um-role-badge"
                          style={{ background: roleStyle.bg, color: roleStyle.color }}
                        >
                          {user.role}
                        </span>
                      </div>
                    </div>
                    <div className="um-card-details">
                      <div className="um-card-row">
                        <FaEnvelope className="um-card-icon" />
                        <span className="um-card-val">{user.email || '—'}</span>
                      </div>
                      <div className="um-card-row">
                        <FaPhone className="um-card-icon" />
                        <span className="um-card-val">{user.phone || '—'}</span>
                      </div>
                      <div className="um-card-row">
                        <FaShieldAlt className="um-card-icon" />
                        <span className="um-card-val">
                          <span className={`um-badge ${user.isActive ? 'um-badge--active' : 'um-badge--inactive'}`}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </span>
                      </div>
                    </div>
                    <div className="um-card-footer">
                      <button className="um-card-btn um-card-btn--view"   onClick={() => handleShowViewModal(user)}><FaEye /> View</button>
                      <button className="um-card-btn um-card-btn--edit"   onClick={() => { setEditingUser(user); setShowModal(true) }}><FaEdit /> Edit</button>
                      <button className="um-card-btn um-card-btn--delete" onClick={() => { setUserToDelete(user); setDeleteModal(true) }} disabled={user.role === 'ADMIN'}><FaTrash /></button>
                    </div>
                  </motion.div>
                </Col>
              )
            })}
          </AnimatePresence>
        </Row>
      )}

      {/* ADD / EDIT MODAL */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Formik
          initialValues={initialValues}
          validationSchema={UserSchema}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            try {
              if (editingUser) {
                await dispatch(updateUser({ id: editingUser.id, ...values })).unwrap()
              } else {
                await dispatch(addUser(values)).unwrap()
              }
              handleCloseModal()
            } catch (err) {
              console.error('Failed to save user:', err)
            } finally {
              setSubmitting(false)
            }
          }}
        >
          {({ errors, touched, isSubmitting }) => (
            <FormikForm>
              <Modal.Header className="um-modal-hero" as="div">
                <button type="button" className="um-modal-close" onClick={handleCloseModal} aria-label="Close">✕</button>
                <div className="um-modal-hero-icon">
                  {editingUser ? <FaEdit /> : <FaUsers />}
                </div>
                <div className="um-modal-hero-text">
                  <h2 className="um-modal-hero-title">{editingUser ? 'Edit User' : 'Add New User'}</h2>
                  <p className="um-modal-hero-sub">
                    {editingUser ? `Updating account for ${editingUser.name}` : 'Fill in the details to create a new user account'}
                  </p>
                </div>
              </Modal.Header>
              <Modal.Body className="um-modal-body">
                <Row className="g-3">
                  <Col md={6}>
                    <div className="um-form-group">
                      <label className="um-label">Full Name <span className="um-required">*</span></label>
                      <Field name="name" className={`um-input ${touched.name && errors.name ? 'um-input--error' : ''}`} placeholder="e.g. John Doe" />
                      {touched.name && errors.name && <span className="um-error">{errors.name}</span>}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="um-form-group">
                      <label className="um-label"><FaEnvelope className="um-lbl-icon" /> Email <span className="um-required">*</span></label>
                      <Field name="email" type="email" className={`um-input ${touched.email && errors.email ? 'um-input--error' : ''}`} placeholder="john@example.com" />
                      {touched.email && errors.email && <span className="um-error">{errors.email}</span>}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="um-form-group">
                      <label className="um-label"><FaPhone className="um-lbl-icon" /> Phone</label>
                      <Field name="phone" className="um-input" placeholder="+91 00000 00000" />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="um-form-group">
                      <label className="um-label">
                        Password {editingUser ? <span className="um-lbl-opt">(leave blank to keep)</span> : <span className="um-required">*</span>}
                      </label>
                      <Field name="password" type="password" className={`um-input ${touched.password && errors.password ? 'um-input--error' : ''}`} placeholder="••••••••" />
                      {touched.password && errors.password && <span className="um-error">{errors.password}</span>}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="um-form-group">
                      <label className="um-label"><FaShieldAlt className="um-lbl-icon" /> Role <span className="um-required">*</span></label>
                      <Field as="select" name="role" className={`um-input um-select ${touched.role && errors.role ? 'um-input--error' : ''}`}>
                        {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </Field>
                      {touched.role && errors.role && <span className="um-error">{errors.role}</span>}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="um-form-group">
                      <label className="um-label"><FaIdCard className="um-lbl-icon" /> Aadhar Number</label>
                      <Field name="aadharNumber" className="um-input" placeholder="0000 0000 0000" />
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="um-form-group">
                      <label className="um-label"><FaMapMarkerAlt className="um-lbl-icon" /> Address</label>
                      <Field as="textarea" name="address" rows={3} className="um-input um-textarea" placeholder="Full address" />
                    </div>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer className="um-modal-footer">
                <div className="um-modal-footer-note"><span className="um-required">*</span> Required field</div>
                <div className="um-modal-footer-actions">
                  <button type="button" className="um-btn-cancel" onClick={handleCloseModal} disabled={isSubmitting}>Cancel</button>
                  <button type="submit" className="um-btn-submit" disabled={isSubmitting}>
                    {isSubmitting
                      ? <><Spinner size="sm" animation="border" className="me-2" />Saving…</>
                      : <>{editingUser ? <><FaEdit className="me-2" />Update User</> : <><FaPlus className="me-2" />Save User</>}</>}
                  </button>
                </div>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>

      {/* VIEW MODAL */}
      <Modal show={!!viewUser} onHide={handleCloseViewModal} centered>
        <Modal.Header closeButton className="um-modal-header">
          <Modal.Title className="um-modal-title">User Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="um-modal-body">
          {viewUser && (
            <div className="um-view-grid">
              <div className="um-view-avatar">{viewUser.name?.charAt(0).toUpperCase()}</div>
              <div className="um-view-name">{viewUser.name}</div>
              <span
                className="um-role-badge"
                style={{ background: (ROLE_COLORS[viewUser.role] || {}).bg || '#f4f6fa', color: (ROLE_COLORS[viewUser.role] || {}).color || '#718096' }}
              >
                {viewUser.role}
              </span>
              <div className="um-view-rows">
                {[
                  { icon: <FaEnvelope />,    label: 'Email',   val: viewUser.email },
                  { icon: <FaPhone />,        label: 'Phone',   val: viewUser.phone },
                  { icon: <FaIdCard />,       label: 'Aadhar',  val: viewUser.aadharNumber },
                  { icon: <FaShieldAlt />,    label: 'Status',  val: viewUser.isActive ? 'Active' : 'Inactive' },
                  { icon: <FaMapMarkerAlt />, label: 'Address', val: viewUser.address },
                ].map((row, i) => (
                  <div key={i} className="um-view-row">
                    <span className="um-view-icon">{row.icon}</span>
                    <div className="um-view-detail">
                      <span className="um-view-lbl">{row.label}</span>
                      <span className="um-view-val">{row.val || '—'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* DELETE MODAL */}
      <Modal show={deleteModal} onHide={() => setDeleteModal(false)} centered>
        <Modal.Header closeButton className="um-modal-header">
          <Modal.Title className="um-modal-title">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="um-modal-body um-delete-body">
          <div className="um-delete-icon"><FaTrash /></div>
          <p className="um-delete-msg">
            Are you sure you want to delete <strong>{userToDelete?.name}</strong>?
            <br /><span className="um-delete-warn">This action cannot be undone.</span>
          </p>
          <div className="um-delete-actions">
            <button className="um-btn-cancel" onClick={() => setDeleteModal(false)}>Cancel</button>
            <button className="um-btn-delete" onClick={confirmDelete}>Delete</button>
          </div>
        </Modal.Body>
      </Modal>

    </div>
  )
}

export default UserManagement
